import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { create } from 'zustand';
import { Icon } from '../ui/Icon';
import { ActionStream } from '../../features/oggi/ActionStream';
import { useActionStream } from '../../features/oggi/useActionStream';
import { useActionStreamStore } from '../../stores/actionStream.store';
import { useModalStore } from '../../stores/modal.store';

/**
 * ActionPanel — slide-over destro "Da fare" (sostituisce il dropdown campanella).
 *
 * Montato una volta sola in AppShell, fuori dal flusso del main; il contenuto
 * è lo stesso ActionStream della pagina Oggi in variante compatta (stessa
 * cache TanStack, zero fetch extra). Apertura/chiusura via store zustand
 * locale esportato (stesso pattern di modal.store: stato effimero di shell,
 * NON persistito — a differenza di ui.store non deve sopravvivere al reload).
 *
 * Interazioni:
 *   · Ctrl+. (period) — toggle da ovunque (guardia input/contenteditable)
 *   · Esc             — chiude (ignorato se c'è un modale sopra)
 *   · click sullo scrim / bottone X — chiude
 *   · cambio route    — chiude (come faceva il dropdown notifiche al click)
 *
 * "Archivia le FYI": archivio locale in blocco delle notifiche band 3 via
 * actionStream.store — le toglie dallo stream per sempre su questo browser
 * (il read server-side resta compito della E sulla singola riga).
 */

interface ActionPanelState {
  open: boolean;
  toggle: () => void;
  setOpen: (open: boolean) => void;
}

export const useActionPanelStore = create<ActionPanelState>((set) => ({
  open: false,
  toggle: () => set((s) => ({ open: !s.open })),
  setOpen: (open) => set({ open }),
}));

/** True se l'utente sta digitando: la shortcut globale non deve interferire. */
function typingTarget(target: EventTarget | null): boolean {
  const t = target as HTMLElement | null;
  return !!t && (['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName) || t.isContentEditable || !!t.closest('[contenteditable="true"]'));
}

export function ActionPanel() {
  const open = useActionPanelStore((s) => s.open);
  const setOpen = useActionPanelStore((s) => s.setOpen);
  const toggle = useActionPanelStore((s) => s.toggle);
  const archiveFyi = useActionStreamStore((s) => s.archiveFyi);
  const { bands, counts } = useActionStream();
  const location = useLocation();
  const panelRef = useRef<HTMLElement>(null);
  const firstRenderRef = useRef(true);

  // Il contenuto resta montato durante la transizione d'uscita (240ms, come
  // .panel-over nel CSS) e si smonta dopo: niente tab-stop su righe fuori
  // schermo né listener tastiera dello stream attivi a pannello chiuso.
  const [rendered, setRendered] = useState(open);
  useEffect(() => {
    if (open) { setRendered(true); return; }
    const t = window.setTimeout(() => setRendered(false), 240);
    return () => window.clearTimeout(t);
  }, [open]);

  // Focus sul pannello all'apertura: Esc e J/K funzionano subito da tastiera
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  // Cambio route → chiudi (le righe navigano senza chiudere da sole)
  useEffect(() => {
    if (firstRenderRef.current) { firstRenderRef.current = false; return; }
    setOpen(false);
  }, [location.pathname, setOpen]);

  // Shortcut globali: Ctrl+. toggla da ovunque, Esc chiude
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '.' && (e.ctrlKey || e.metaKey) && !e.altKey) {
        if (typingTarget(e.target)) return;
        e.preventDefault();
        toggle();
        return;
      }
      if (e.key === 'Escape' && useActionPanelStore.getState().open) {
        // un modale aperto sopra il pannello ha la precedenza sull'Esc
        if (useModalStore.getState().kind || document.querySelector('.modal-backdrop')) return;
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle, setOpen]);

  // Conteggio header = item azionabili visibili (le FYI restano dietro la chip)
  const total = counts.critical + counts.today + counts.week;
  const fyiKeys = (bands.find((b) => b.band === 3)?.items ?? []).map((i) => i.key);

  return (
    <>
      <div
        className="panel-over-scrim"
        data-open={open || undefined}
        aria-hidden="true"
        onClick={() => setOpen(false)}
      />
      <aside
        ref={panelRef}
        className="panel-over"
        data-open={open || undefined}
        role="dialog"
        aria-label="Da fare"
        aria-hidden={!open}
        tabIndex={-1}
      >
        {/* Contenuto montato solo da aperto (+ transizione d'uscita): a pannello
            chiuso nessun bottone resta raggiungibile con Tab da fuori schermo */}
        {rendered && (
          <>
            <div className="panel-over-hd">
              <div className="flex items-center gap-2 min-w-0">
                <div className="t-h3">Da fare</div>
                {total > 0 && <span className="mono text-[12px] mute">— {total}</span>}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {fyiKeys.length > 0 && (
                  <button
                    className="btn btn-sm btn-ghost"
                    title="Archivia tutte le notifiche FYI (le toglie dallo stream)"
                    onClick={() => archiveFyi(fyiKeys)}
                  >
                    Archivia le FYI
                  </button>
                )}
                <button
                  className="btn btn-sm btn-ghost btn-icon"
                  aria-label="Chiudi"
                  title="Chiudi (Esc)"
                  onClick={() => setOpen(false)}
                >
                  <Icon name="x" size={14} />
                </button>
              </div>
            </div>
            <div className="panel-over-bd">
              <ActionStream compact />
            </div>
          </>
        )}
      </aside>
    </>
  );
}
